import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  FormControl,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Autocomplete,
  Chip,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import OrganizationTypesField from "../../components/company/OrganizationTypesField";
import PageContainer from "../../components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyByIdQuery,
  useAssignUsersToCompanyMutation,
  useRemoveUsersFromCompanyMutation,
} from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { Company } from "../../types/CompanyTypes";
import { BillingManagement } from "../../components/billing";
import { AudienceManagement } from "../../components/audiences";
import { BRAND_PILL_INK } from "../../theme/theme";
import * as Sentry from "@sentry/react";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

// Define a more comprehensive interface for the form data
interface CompanyFormData extends Omit<
  Partial<Company>,
  "feeVariable" | "feeFixed"
> {
  feeVariable?: number | string;
  feeFixed?: number | string;
}

// Campos que la empresa no puede guardar vacíos, en el orden en que salen en
// pantalla. Los datos fiscales y de contacto no están aquí: viven en el billing,
// que se edita más abajo con su propio formulario.
const REQUIRED_FIELDS = ["nameCommercial", "nameFiscal", "nif"] as const;

type RequiredField = (typeof REQUIRED_FIELDS)[number];

const EditCompany: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { data: companyData } = useGetCompanyByIdQuery(id!);
  const [
    updateCompany,
    {
      isLoading: isUpdating,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateCompanyMutation();

  const [
    deleteCompany,
    {
      isLoading: isDeleting,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
      error: deleteError,
    },
  ] = useDeleteCompanyMutation();
  const [
    assignUsersToCompany,
    {
      isLoading: isAssigning,
      isSuccess: isAssignSuccess,
      isError: isAssignError,
      error: assignError,
    },
  ] = useAssignUsersToCompanyMutation();

  const [
    removeUsersFromCompany,
    { isSuccess: isRemoveSuccess, isError: isRemoveError, error: removeError },
  ] = useRemoveUsersFromCompanyMutation();
  const { data: usersData } = useGetUsersQuery();

  const [uploading, setUploading] = useState(false);
  // Separate company data from billing data
  const [formData, setFormData] = useState<CompanyFormData>({
    nameCommercial: "",
    nameFiscal: "",
    nif: "",
    feeVariable: "0.00",
    feeFixed: "0.00",
    paymentTerms: "",
    logoUrl: "",
    imageBase64: "",
    dv360AdvertiserIds: [],
    outbrainAccountIds: [],
    taboolaAccountIds: [],
    spotifyAdAccountIds: [],
    hivestackAdvertiserIds: [],
    amazonAdvertiserIds: [],
  });

  // Track validation errors for integration fields
  const [integrationErrors, setIntegrationErrors] = useState<{
    dv360AdvertiserIds?: string;
    outbrainAccountIds?: string;
    taboolaAccountIds?: string;
    spotifyAdAccountIds?: string[];
    hivestackAdvertiserIds?: string[];
    amazonAdvertiserIds?: string[];
    metaAdAccountIds?: string[];
    tiktokAdvertiserIds?: string[];
    googleAdsCustomerIds?: string[];
    redditAdAccountIds?: string[];
  }>({});

  // Required fields left empty on the last submit attempt, highlighted in the form
  const [missingRequiredFields, setMissingRequiredFields] = useState<
    RequiredField[]
  >([]);

  const [previewImage, setPreviewImage] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Get assigned users from company data
  const companyUsers = companyData?.data?.users || [];

  // Filter available users (users not already assigned to the company)
  const assignedUserIds = companyUsers.map((cu) => cu.user.id);
  const filteredUsers =
    usersData?.data?.filter(
      (user) =>
        (user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        !assignedUserIds.includes(user.id)
    ) || [];

  // Handle user selection for assignment
  const handleUserSelect = (userId: string) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle assigning users to company
  const handleAssignUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      await assignUsersToCompany({
        id: id!,
        userIds: selectedUsers,
      }).unwrap();

      // Clear selected users after successful assignment
      setSelectedUsers([]);

      dispatch(
        setToastMessage({
          message: t("company.success.assignUsersSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      let errorMessage = t("common.unknownError");
      if (typeof error === "object" && error !== null && "data" in error) {
        const errorData = error.data as { message?: string };
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      dispatch(
        setToastMessage({
          message: t("company.errors.assignUsersError", {
            error: errorMessage,
          }),
          severity: "error",
        })
      );
    }
  };

  // Handle removing users from company
  const handleRemoveUsers = async (userIds: string[]) => {
    if (userIds.length === 0) return;

    try {
      await removeUsersFromCompany({
        id: id!,
        userIds,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("company.success.removeUsersSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      let errorMessage = t("common.unknownError");
      if (typeof error === "object" && error !== null && "data" in error) {
        const errorData = error.data as { message?: string };
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      dispatch(
        setToastMessage({
          message: t("company.errors.removeUsersError", {
            error: errorMessage,
          }),
          severity: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (companyData?.data) {
      const company = companyData.data;

      // Initialize with company data
      const initialFormData: CompanyFormData = {
        nameCommercial: company.nameCommercial,
        organizationTypes: company.organizationTypes ?? [],
        nameFiscal: company.nameFiscal,
        nif: company.nif,
        feeVariable:
          company.feeVariable != null
            ? Number(company.feeVariable).toFixed(2)
            : "0.00",
        feeFixed:
          company.feeFixed != null
            ? Number(company.feeFixed).toFixed(2)
            : "0.00",
        paymentTerms: company.paymentTerms ?? "",
        logoUrl: company.logoUrl,
        imageBase64: company.imageBase64 || "", // Preserve the existing imageBase64
        dv360AdvertiserIds: company.dv360AdvertiserIds || [],
        outbrainAccountIds: company.outbrainAccountIds || [],
        taboolaAccountIds: company.taboolaAccountIds || [],
        spotifyAdAccountIds: company.spotifyAdAccountIds || [],
        hivestackAdvertiserIds: company.hivestackAdvertiserIds || [],
        amazonAdvertiserIds: company.amazonAdvertiserIds || [],
        metaAdAccountIds: company.metaAdAccountIds || [],
        tiktokAdvertiserIds: company.tiktokAdvertiserIds || [],
        googleAdsCustomerIds: company.googleAdsCustomerIds || [],
        redditAdAccountIds: company.redditAdAccountIds || [],
      };

      setFormData(initialFormData);

      if (company.imageBase64) {
        setPreviewImage(`data:image/png;base64,${company.imageBase64}`);
      }
    }
  }, [companyData]);

  // Step 1: Handle successful company update
  useEffect(() => {
    if (isUpdateSuccess) {
      console.log("Company updated successfully");
      dispatch(
        setToastMessage({
          message: t("company.success.updateSuccess"),
          severity: "success",
        })
      );
      navigate("/companies");
    }
  }, [isUpdateSuccess, dispatch, t, navigate]);

  // Step 2: Handle company update error
  useEffect(() => {
    if (isUpdateError) {
      console.error("Update error detected:", updateError);

      // Show a generic error message to the user
      dispatch(
        setToastMessage({
          message: t("company.errors.updateError", { error: "" }),
          severity: "error",
        })
      );
    }
  }, [isUpdateError, updateError, dispatch, t]);

  // Step 3: Handle delete success
  useEffect(() => {
    if (isDeleteSuccess) {
      console.log("Company deleted successfully");
      dispatch(
        setToastMessage({
          message: t("company.success.deleteSuccess"),
          severity: "success",
        })
      );
      navigate("/companies");
    }
  }, [isDeleteSuccess, dispatch, t, navigate]);

  // Step 4: Handle delete error
  useEffect(() => {
    if (isDeleteError) {
      console.error("Delete error detected:", deleteError);

      // Show a generic error message to the user
      dispatch(
        setToastMessage({
          message: t("company.errors.deleteError", { error: "" }),
          severity: "error",
        })
      );
    }
  }, [isDeleteError, deleteError, dispatch, t]);

  // Step 5: Handle user assignment success
  useEffect(() => {
    if (isAssignSuccess) {
      console.log("Users assigned successfully");
      dispatch(
        setToastMessage({
          message: t("company.success.assignUsersSuccess"),
          severity: "success",
        })
      );
    }
  }, [isAssignSuccess, dispatch, t]);

  // Step 8: Handle user assignment error
  useEffect(() => {
    if (isAssignError) {
      console.error("User assignment error detected:", assignError);

      dispatch(
        setToastMessage({
          message: t("company.errors.assignUsersError", { error: "" }),
          severity: "error",
        })
      );
    }
  }, [isAssignError, assignError, dispatch, t]);

  // Step 9: Handle user removal success
  useEffect(() => {
    if (isRemoveSuccess) {
      console.log("Users removed successfully");
      dispatch(
        setToastMessage({
          message: t("company.success.removeUsersSuccess"),
          severity: "success",
        })
      );
    }
  }, [isRemoveSuccess, dispatch, t]);

  // Step 10: Handle user removal error
  useEffect(() => {
    if (isRemoveError) {
      console.error("User removal error detected:", removeError);

      dispatch(
        setToastMessage({
          message: t("company.errors.removeUsersError", { error: "" }),
          severity: "error",
        })
      );
    }
  }, [isRemoveError, removeError, dispatch, t]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.[0]) {
      setUploading(true);
      try {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64WithoutPrefix = base64String.split(",")[1];
          setFormData({
            ...formData,
            imageBase64: base64WithoutPrefix,
          });
          setPreviewImage(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error uploading image:", error);
        dispatch(
          setToastMessage({
            message: t("company.errors.imageUploadError"),
            severity: "error",
          })
        );
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id) {
      dispatch(
        setToastMessage({
          message: t("common.errors.missingId", {
            defaultValue: "Company ID is missing",
          }),
          severity: "error",
        })
      );
      return;
    }

    const missingFields = REQUIRED_FIELDS.filter(
      (field) => !String(formData[field] ?? "").trim()
    );

    if (missingFields.length > 0) {
      setMissingRequiredFields(missingFields);

      dispatch(
        setToastMessage({
          message: t("company.errors.missingFieldsDetail", {
            fields: missingFields
              .map((field) => t(`company.${field}`))
              .join(", "),
            defaultValue:
              "Please fill in all required fields before submitting",
          }),
          severity: "error",
        })
      );

      document
        .getElementById(`company-field-${missingFields[0]}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return;
    }

    setMissingRequiredFields([]);

    // Check for duplicate integration IDs before submission
    const {
      dv360AdvertiserIds,
      outbrainAccountIds,
      taboolaAccountIds,
      spotifyAdAccountIds,
      hivestackAdvertiserIds,
      amazonAdvertiserIds,
      metaAdAccountIds,
      tiktokAdvertiserIds,
      googleAdsCustomerIds,
      redditAdAccountIds,
    } = formData;
    const integrationIds = [
      ...(dv360AdvertiserIds || []),
      ...(outbrainAccountIds || []),
      ...(taboolaAccountIds || []),
      ...(spotifyAdAccountIds || []),
      ...(hivestackAdvertiserIds || []),
      ...(amazonAdvertiserIds || []),
      ...(metaAdAccountIds || []),
      ...(tiktokAdvertiserIds || []),
      ...(googleAdsCustomerIds || []),
      ...(redditAdAccountIds || []),
    ].filter((id) => id?.trim() !== "");
    const uniqueIds = new Set(integrationIds);

    if (integrationIds.length !== uniqueIds.size) {
      // Duplicate IDs found, validate all fields to show errors
      const newErrors: { [key: string]: string } = {};

      // Find duplicate values and mark them as errors
      const idCounts: { [key: string]: string[] } = {};

      (dv360AdvertiserIds || []).forEach((id) => {
        if (id) {
          idCounts[id] = [...(idCounts[id] || []), "dv360AdvertiserIds"];
        }
      });

      (outbrainAccountIds || []).forEach((id) => {
        if (id) {
          idCounts[id] = [...(idCounts[id] || []), "outbrainAccountIds"];
        }
      });

      (taboolaAccountIds || []).forEach((id) => {
        if (id) {
          idCounts[id] = [...(idCounts[id] || []), "taboolaAccountIds"];
        }
      });

      if (spotifyAdAccountIds && spotifyAdAccountIds.length > 0) {
        spotifyAdAccountIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "spotifyAdAccountIds"];
        });
      }

      if (hivestackAdvertiserIds && hivestackAdvertiserIds.length > 0) {
        hivestackAdvertiserIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "hivestackAdvertiserIds"];
        });
      }

      if (amazonAdvertiserIds && amazonAdvertiserIds.length > 0) {
        amazonAdvertiserIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "amazonAdvertiserIds"];
        });
      }

      if (metaAdAccountIds && metaAdAccountIds.length > 0) {
        metaAdAccountIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "metaAdAccountIds"];
        });
      }

      if (tiktokAdvertiserIds && tiktokAdvertiserIds.length > 0) {
        tiktokAdvertiserIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "tiktokAdvertiserIds"];
        });
      }

      if (googleAdsCustomerIds && googleAdsCustomerIds.length > 0) {
        googleAdsCustomerIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "googleAdsCustomerIds"];
        });
      }

      if (redditAdAccountIds && redditAdAccountIds.length > 0) {
        redditAdAccountIds.forEach((id) => {
          idCounts[id] = [...(idCounts[id] || []), "redditAdAccountIds"];
        });
      }

      // Mark fields with duplicate values
      Object.entries(idCounts).forEach(([, fields]) => {
        if (fields.length > 1) {
          fields.forEach((field) => {
            newErrors[field] = t("company.errors.duplicateIntegrationId", {
              defaultValue: "This ID is already used for another integration",
            });
          });
        }
      });

      setIntegrationErrors(newErrors);

      // Show toast message
      dispatch(
        setToastMessage({
          message: t("company.errors.duplicateIntegrationIds", {
            defaultValue:
              "Please fix duplicate integration IDs before submitting",
          }),
          severity: "error",
        })
      );

      return;
    }

    const billings = companyData?.data?.billings ?? [];
    const defaultBilling = billings.find((b) => b.isDefault) || billings[0];

    // Create a clean object with only the company fields
    // Explicitly excluding users, id, createdAt, and updatedAt
    const updateData: Partial<Company> = {
      nameCommercial: formData.nameCommercial,
      organizationTypes: formData.organizationTypes ?? [],
      nameFiscal: formData.nameFiscal,
      nif: formData.nif,
      logoUrl: formData.logoUrl || "",
      dv360AdvertiserIds: formData.dv360AdvertiserIds || [],
      outbrainAccountIds: formData.outbrainAccountIds || [],
      taboolaAccountIds: formData.taboolaAccountIds || [],
      spotifyAdAccountIds: formData.spotifyAdAccountIds || [],
      hivestackAdvertiserIds: formData.hivestackAdvertiserIds || [],
      amazonAdvertiserIds: formData.amazonAdvertiserIds || [],
      metaAdAccountIds: formData.metaAdAccountIds || [],
      tiktokAdvertiserIds: formData.tiktokAdvertiserIds || [],
      googleAdsCustomerIds: formData.googleAdsCustomerIds || [],
      redditAdAccountIds: formData.redditAdAccountIds || [],
      // Duplican lo que ya vive en el billing por defecto, así que se copian de
      // allí en lugar de pedirse otra vez: así no pueden quedar descuadrados.
      email: defaultBilling?.email ?? "",
      phone: defaultBilling?.phoneNumber ?? "",
      feeVariable:
        formData.feeVariable !== "" && formData.feeVariable != null
          ? parseFloat(String(formData.feeVariable))
          : 0,
      feeFixed:
        formData.feeFixed !== "" && formData.feeFixed != null
          ? parseFloat(String(formData.feeFixed))
          : 0,
      paymentTerms: formData.paymentTerms || "",
    };

    // Only include imageBase64 if it has been changed (a new image was uploaded)
    if (formData.imageBase64) {
      updateData.imageBase64 = formData.imageBase64;
    }

    try {
      await updateCompany({ id, updates: updateData }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating company:", error);
      // Error will be handled by the useEffect hook for updateError
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteCompany(id).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error deleting company:", error);
      // Error will be handled by the useEffect hook for deleteError
    }
  };

  const handleInputChange =
    (field: keyof CompanyFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      setFormData({
        ...formData,
        [field]: newValue,
      });

      if (newValue.trim()) {
        setMissingRequiredFields((previous) =>
          previous.filter((missingField) => missingField !== field)
        );
      }
    };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: BRAND_PILL_INK,
                  borderRadius: "16px",
                  px: 3,
                  py: 1.5,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.secondary50.main,
                  }}
                >
                  {formData.nameCommercial}
                </Typography>
                <EditIcon sx={{ color: theme.palette.secondary50.main }} />
              </Box>
              <Button
                startIcon={<KeyboardBackspaceIcon />}
                onClick={() => navigate("/companies")}
                sx={{
                  height: "fit-content",
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[900],
                  "&:hover": {
                    borderColor: theme.palette.grey[300],
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                {t("common.back")}
              </Button>
            </Box>

            {/* Logo Upload Section */}
            <Box sx={{ mb: 4 }}>
              <FormControl>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 9999,
                      border: `2px solid ${theme.palette.grey[200]}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    {uploading ? (
                      <CircularProgress size={40} />
                    ) : previewImage ? (
                      <Box
                        component="img"
                        src={previewImage}
                        alt={formData.nameCommercial || t("company.logo")}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PhotoCamera
                        sx={{ color: theme.palette.grey[400], fontSize: 40 }}
                      />
                    )}
                  </Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    id="company-logo-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="company-logo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      disabled={uploading}
                      sx={{
                        borderRadius: 50,
                        textTransform: "none",
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderColor: theme.palette.grey[200],
                        color: theme.palette.grey[900],
                        "&:hover": {
                          borderColor: theme.palette.grey[300],
                          backgroundColor: theme.palette.background.paper,
                        },
                      }}
                    >
                      {uploading ? (
                        <CircularProgress size={24} />
                      ) : (
                        t("company.uploadLogo")
                      )}
                    </Button>
                  </label>
                </Box>
              </FormControl>
            </Box>

            {/* Company Details Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.nameCommercial")}
                    <Box
                      component="span"
                      sx={{ color: theme.palette.error.main, ml: 0.5 }}
                    >
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="company-field-nameCommercial"
                    value={formData.nameCommercial}
                    onChange={handleInputChange("nameCommercial")}
                    fullWidth
                    variant="outlined"
                    error={missingRequiredFields.includes("nameCommercial")}
                    helperText={
                      missingRequiredFields.includes("nameCommercial")
                        ? t("company.errors.requiredField")
                        : ""
                    }
                    inputProps={{ "aria-required": true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: missingRequiredFields.includes(
                            "nameCommercial"
                          )
                            ? theme.palette.error.main
                            : theme.palette.neutral[400],
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        fontFamily: "Nunito",
                        ml: 2,
                      },
                    }}
                  />
                </Box>
                <OrganizationTypesField
                  value={formData.organizationTypes ?? []}
                  onChange={(next) =>
                    setFormData((prev) => ({
                      ...prev,
                      organizationTypes: next,
                    }))
                  }
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.nameFiscal")}
                    <Box
                      component="span"
                      sx={{ color: theme.palette.error.main, ml: 0.5 }}
                    >
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="company-field-nameFiscal"
                    value={formData.nameFiscal}
                    onChange={handleInputChange("nameFiscal")}
                    fullWidth
                    variant="outlined"
                    error={missingRequiredFields.includes("nameFiscal")}
                    helperText={
                      missingRequiredFields.includes("nameFiscal")
                        ? t("company.errors.requiredField")
                        : ""
                    }
                    inputProps={{ "aria-required": true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: missingRequiredFields.includes(
                            "nameFiscal"
                          )
                            ? theme.palette.error.main
                            : theme.palette.neutral[400],
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        fontFamily: "Nunito",
                        ml: 2,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.nif")}
                    <Box
                      component="span"
                      sx={{ color: theme.palette.error.main, ml: 0.5 }}
                    >
                      *
                    </Box>
                  </Typography>
                  <TextField
                    id="company-field-nif"
                    value={formData.nif}
                    onChange={handleInputChange("nif")}
                    fullWidth
                    variant="outlined"
                    error={missingRequiredFields.includes("nif")}
                    helperText={
                      missingRequiredFields.includes("nif")
                        ? t("company.errors.requiredField")
                        : ""
                    }
                    inputProps={{ "aria-required": true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: missingRequiredFields.includes("nif")
                            ? theme.palette.error.main
                            : theme.palette.neutral[400],
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        fontFamily: "Nunito",
                        ml: 2,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.feeVariable")}
                  </Typography>
                  <TextField
                    value={formData.feeVariable}
                    onChange={handleInputChange("feeVariable")}
                    fullWidth
                    type="number"
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        max: 100,
                        step: "0.01",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.neutral[400],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.feeFixed")}
                  </Typography>
                  <TextField
                    value={formData.feeFixed}
                    onChange={handleInputChange("feeFixed")}
                    fullWidth
                    type="number"
                    variant="outlined"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: "0.01",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.neutral[400],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.paymentTerms")}
                  </Typography>
                  <TextField
                    select
                    value={formData.paymentTerms ?? ""}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        paymentTerms: event.target.value,
                      })
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.neutral[400],
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      {t("company.paymentTermsUndefined")}
                    </MenuItem>
                    <MenuItem value="30">30</MenuItem>
                    <MenuItem value="45">45</MenuItem>
                    <MenuItem value="60">60</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </Box>

            {/* Billing Management Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              {id && <BillingManagement companyId={id} />}
            </Box>

            {/* Audiences Section */}
            {id && <AudienceManagement companyId={id} />}

            {/* Integrations Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                backgroundColor: theme.palette.neutral["100"],
                borderRadius: "8px",
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.dv360AdvertiserId")}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.dv360AdvertiserIds}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        dv360AdvertiserIds: newValue,
                      }));
                      if (integrationErrors.dv360AdvertiserIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.dv360AdvertiserIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!integrationErrors.dv360AdvertiserIds}
                        placeholder={t("company.dv360AdvertiserId") || ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.dv360AdvertiserIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.dv360AdvertiserIds && (
                    <FormHelperText error>
                      {integrationErrors.dv360AdvertiserIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.outbrainAccountId")}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.outbrainAccountIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        outbrainAccountIds: newValue,
                      }));
                      if (integrationErrors.outbrainAccountIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.outbrainAccountIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!integrationErrors.outbrainAccountIds}
                        placeholder={t("company.outbrainAccountId") || ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.outbrainAccountIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.outbrainAccountIds && (
                    <FormHelperText error>
                      {integrationErrors.outbrainAccountIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.taboolaAccountId")}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.taboolaAccountIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        taboolaAccountIds: newValue,
                      }));
                      if (integrationErrors.taboolaAccountIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.taboolaAccountIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!integrationErrors.taboolaAccountIds}
                        placeholder={t("company.taboolaAccountId") || ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.taboolaAccountIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.taboolaAccountIds && (
                    <FormHelperText error>
                      {integrationErrors.taboolaAccountIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.spotifyAdAccountId")}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.spotifyAdAccountIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        spotifyAdAccountIds: newValue,
                      }));
                      if (integrationErrors.spotifyAdAccountIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.spotifyAdAccountIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!integrationErrors.spotifyAdAccountIds}
                        placeholder={t("company.spotifyAdAccountId") || ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.spotifyAdAccountIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.spotifyAdAccountIds && (
                    <FormHelperText error>
                      {integrationErrors.spotifyAdAccountIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.hivestackAdvertiserId", {
                      defaultValue: "Hivestack Advertiser IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.hivestackAdvertiserIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        hivestackAdvertiserIds: newValue,
                      }));
                      if (integrationErrors.hivestackAdvertiserIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.hivestackAdvertiserIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="hivestackAdvertiserIds"
                        fullWidth
                        error={!!integrationErrors.hivestackAdvertiserIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor:
                                integrationErrors.hivestackAdvertiserIds
                                  ? theme.palette.error.main
                                  : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.hivestackAdvertiserIds && (
                    <FormHelperText error>
                      {integrationErrors.hivestackAdvertiserIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.amazonAdvertiserId", {
                      defaultValue: "Amazon DSP Advertiser IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.amazonAdvertiserIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        amazonAdvertiserIds: newValue,
                      }));
                      if (integrationErrors.amazonAdvertiserIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.amazonAdvertiserIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="amazonAdvertiserIds"
                        fullWidth
                        error={!!integrationErrors.amazonAdvertiserIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.amazonAdvertiserIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.amazonAdvertiserIds && (
                    <FormHelperText error>
                      {integrationErrors.amazonAdvertiserIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.metaAdAccountId", {
                      defaultValue: "Meta Ad Account IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.metaAdAccountIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        metaAdAccountIds: newValue,
                      }));
                      if (integrationErrors.metaAdAccountIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.metaAdAccountIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="metaAdAccountIds"
                        fullWidth
                        error={!!integrationErrors.metaAdAccountIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.metaAdAccountIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.metaAdAccountIds && (
                    <FormHelperText error>
                      {integrationErrors.metaAdAccountIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.tiktokAdvertiserId", {
                      defaultValue: "TikTok Advertiser IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.tiktokAdvertiserIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        tiktokAdvertiserIds: newValue,
                      }));
                      if (integrationErrors.tiktokAdvertiserIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.tiktokAdvertiserIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="tiktokAdvertiserIds"
                        fullWidth
                        error={!!integrationErrors.tiktokAdvertiserIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.tiktokAdvertiserIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.tiktokAdvertiserIds && (
                    <FormHelperText error>
                      {integrationErrors.tiktokAdvertiserIds}
                    </FormHelperText>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.googleAdsCustomerId", {
                      defaultValue: "Google Ads Customer IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.googleAdsCustomerIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        googleAdsCustomerIds: newValue,
                      }));
                      if (integrationErrors.googleAdsCustomerIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.googleAdsCustomerIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="googleAdsCustomerIds"
                        fullWidth
                        error={!!integrationErrors.googleAdsCustomerIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor:
                                integrationErrors.googleAdsCustomerIds
                                  ? theme.palette.error.main
                                  : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.googleAdsCustomerIds && (
                    <FormHelperText error>
                      {integrationErrors.googleAdsCustomerIds}
                    </FormHelperText>
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("company.redditAdAccountId", {
                      defaultValue: "Reddit Ad Account IDs",
                    })}
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.redditAdAccountIds || []}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        redditAdAccountIds: newValue,
                      }));
                      if (integrationErrors.redditAdAccountIds) {
                        setIntegrationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.redditAdAccountIds;
                          return newErrors;
                        });
                      }
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="redditAdAccountIds"
                        fullWidth
                        error={!!integrationErrors.redditAdAccountIds}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "15px",
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: integrationErrors.redditAdAccountIds
                                ? theme.palette.error.main
                                : theme.palette.neutral[400],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  {integrationErrors.redditAdAccountIds && (
                    <FormHelperText error>
                      {integrationErrors.redditAdAccountIds}
                    </FormHelperText>
                  )}
                </Box>
              </Box>
            </Box>

            {/* User Assignment Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                  pb: 1,
                  mb: 1,
                }}
              >
                {t("company.assignUsers")}
              </Typography>

              {/* Search and Assign Users */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 1,
                  }}
                >
                  {t("company.searchUsers")}
                </Typography>

                <TextField
                  placeholder={t("company.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.neutral[400],
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {searchTerm.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      borderRadius: 2,
                      mt: 2,
                    }}
                  >
                    {filteredUsers.length > 0 ? (
                      <List dense>
                        {filteredUsers.map((user) => (
                          <ListItem
                            key={user.id}
                            onClick={() => handleUserSelect(user.id)}
                            sx={{
                              "&:hover": {
                                backgroundColor: theme.palette.grey[100],
                              },
                              cursor: "pointer",
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selectedUsers.includes(user.id)}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{ fontFamily: "Nunito", fontWeight: 600 }}
                                >
                                  {`${user.firstName} ${user.lastName}`}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.grey[600] }}
                                >
                                  {user.email}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: 3,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{ fontFamily: "Nunito", textAlign: "center" }}
                        >
                          {t("company.noUsersFound")}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {selectedUsers.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleAssignUsers}
                    disabled={isAssigning}
                    startIcon={
                      isAssigning ? null : <i className="fas fa-user-plus" />
                    }
                    sx={{
                      borderRadius: 50,
                      textTransform: "none",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      px: 3,
                      py: 1.5,
                      mt: 2,
                    }}
                  >
                    {isAssigning ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      `${t("company.assignSelectedUsers")} (${
                        selectedUsers.length
                      })`
                    )}
                  </Button>
                )}
              </Box>

              {/* Assigned Users */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 2,
                  }}
                >
                  {t("company.assignedUsers")}
                </Typography>

                {companyUsers.length > 0 ? (
                  <>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 300, overflow: "auto", borderRadius: 2 }}
                    >
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("company.name")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("company.email")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            ></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {companyUsers.map((user) => (
                            <TableRow
                              key={user.user.id}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">
                                <Typography
                                  sx={{ fontFamily: "Nunito", fontWeight: 600 }}
                                >
                                  {`${user.user.firstName} ${user.user.lastName}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.grey[600] }}
                                >
                                  {user.user.email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="text"
                                  onClick={() =>
                                    handleRemoveUsers([user.user.id])
                                  }
                                  startIcon={<DeleteIcon />}
                                  sx={{
                                    color: theme.palette.error.main,
                                    textTransform: "none",
                                    fontFamily: "Nunito",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t("company.remove")}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: "Nunito", textAlign: "center" }}
                    >
                      {t("company.noUsersAssigned")}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/companies")}
                startIcon={<KeyboardBackspaceIcon />}
                sx={{
                  color: theme.palette.grey[900],
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                }}
              >
                {t("common.back")}
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  startIcon={<DeleteIcon />}
                  disabled={isDeleting}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("company.delete")
                  )}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isUpdating}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.getContrastText(
                      theme.palette.grey[900]
                    ),
                    "&:hover": {
                      backgroundColor: theme.palette.grey[800],
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("company.updateCompany")
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default EditCompany;
